import React, { Fragment } from "react";
import { ChangeEvent } from "react";

import { DateTime } from "luxon";

import { IAuthProps } from "../AuthContext";
import DelayInput from "../DelayInput";

import { V2 } from "../../Api";
import withAuth from "../withAuth";
import withTitle from "../withTitle";
import TabControl from "../TabControl";

interface IMetricsLocationViewState {
    isLoading: boolean | string;
    hasChanged: boolean;
    searchStart: string;
    searchEnd: string;
    searchMonth: string;
    dateType: "month" | "range";
    dateMode: "for" | "on";
    tabIndex: number,

    locations: any[];

    [name: string]: any;
}


class MetricsLocationView extends React.Component<IAuthProps, IMetricsLocationViewState> {
    constructor(props: Readonly<IAuthProps>) {
        super(props);

        const today = DateTime.local();

        this.state = {
            isLoading: true,
            hasChanged: false,
            searchStart: today.startOf("month").toISODate(),
            searchEnd: today.endOf("month").toISODate(),
            searchMonth: today.startOf("month").toFormat("yyyy-MM"),
            dateType: "month",
            dateMode: "for",
            tabIndex: 0,

            locations: []
        };

        this.search = this.search.bind(this);
        this.handleElementChange = this.handleElementChange.bind(this);
        this.handleTabChanged = this.handleTabChanged.bind(this);
    }

    public componentDidMount() {
        this.search();
    }

    public componentDidUpdate(prevProps: Readonly<IAuthProps>, prevState: Readonly<IMetricsLocationViewState>) {
        if (this.state.searchStart !== prevState.searchStart && !this.state.searchStart) {
            // Default back to safe value
            this.setState({
                searchStart: DateTime.local().startOf("month").toISODate()
            }, () => this.search());
        }
        else if (this.state.searchEnd !== prevState.searchEnd && !this.state.searchEnd) {
            // Default back to safe value
            this.setState({
                searchEnd: DateTime.local().endOf("month").toISODate()
            }, () => this.search());
        }
        else if (this.state.searchMonth !== prevState.searchMonth && !this.state.searchMonth) {
            // Default back to safe value
            this.setState({
                searchMonth: DateTime.local().startOf("month").toFormat("yyyy-MM")
            }, () => this.search());
        }
    }

    public render() {
        return (
            <div className="viewer-root">
                <div className="filter-control-box">
                    <select name="dateMode" value={this.state.dateMode} onChange={this.handleElementChange}>
                        <option value="for">Scheduled For</option>
                        <option value="on">Created On</option>
                    </select>
                    <select name="dateType" value={this.state.dateType} onChange={this.handleElementChange}>
                        <option value="month">Month</option>
                        <option value="range">Date Range</option>
                    </select>
                    {
                        this.state.dateType === "range"
                            ? <Fragment>
                                <div className="control-group fill-space">
                                    <span>Start Date:</span>
                                    <DelayInput type="date" timeout={500} value={this.state.searchStart} name="searchStart" onChangeDelayed={this.handleElementChange} />
                                </div>
                                <div className="control-group fill-space">
                                    <span>End Date:</span>
                                    <DelayInput type="date" timeout={500} value={this.state.searchEnd} name="searchEnd" onChangeDelayed={this.handleElementChange} />
                                </div>
                            </Fragment>
                            : <DelayInput type="month" className="fill-space" timeout={500} value={this.state.searchMonth} name="searchMonth" onChangeDelayed={this.handleElementChange} />
                    }
                </div>
                <TabControl headers={["Appointments & Surgeries", "Surgery Cases"]} selectedIndex={this.state.tabIndex} onTabChanged={this.handleTabChanged}>
                    <div>
                        {this.renderTable(true)}
                        <p>
                            <em>
                                <small>
                                    Notes: This displays appointments based on the selected criteria, and surgeries that came from those appointments.
                                </small>
                            </em>
                        </p>
                    </div>
                    <div>
                        {this.renderTableSurgeries()}
                        <p>
                            <em>
                                <small>
                                    Notes: This displays surgeries broken down by the location the office visit was at.<br />
                                    This includes OR, Indigent, and Office cases.
                                </small>
                            </em>
                        </p>
                    </div>
                </TabControl>
            </div>
        );
    }

    private renderTable(showAppointments: boolean) {
        return <table className="table-vertical">
            <thead>
                <tr>
                    <th colSpan={1} />
                    {showAppointments ? <th colSpan={5} className="col-smallest col-center">Appointments</th> : null}
                    <th colSpan={4} className="col-smallest col-center">Surgeries</th>
                </tr>
                <tr>
                    <th rowSpan={2}>Name</th>
                    {showAppointments
                        ? <>
                            <th colSpan={2} className="col-smallest col-center col-bold th-font-tiny">Outstanding</th>

                            <th rowSpan={2} className="col-smallest col-center th-font-tiny">Canceled<br />Noshow</th>
                            <th rowSpan={2} className="col-smallest col-center th-font-tiny">Done</th>
                            <th rowSpan={2} className="col-smallest col-center col-bold th-font-tiny">Total</th>
                        </> : null}
                    <th rowSpan={2} className="col-smallest col-center th-font-tiny">Outstanding</th>
                    <th rowSpan={2} className="col-smallest col-center th-font-tiny">Canceled</th>
                    <th rowSpan={2} className="col-smallest col-center th-font-tiny">Done</th>
                    <th rowSpan={2} className="col-smallest col-center col-bold th-font-tiny">Total</th>
                </tr>
                {showAppointments
                    ? <tr>
                        <th className="col-smallest col-center th-font-tiny">Pending</th>
                        <th className="col-smallest col-center th-font-tiny">Active</th>
                    </tr>
                    : null}
            </thead>
            <thead className="table-vertical-head-small">
                <tr>
                    <th>Location Stats</th>
                </tr>
            </thead>
            <tbody>
                {
                    this.state.isLoading
                        ? <tr>
                            <td colSpan={11}>
                                {
                                    typeof this.state.isLoading === "string"
                                        ? <span className="error-text">{this.state.isLoading}</span>
                                        : <div className="spinner-center lds-dual-ring" />
                                }
                            </td>
                        </tr>
                        : this.state.locations.length < 1
                            ? <tr>
                                <td colSpan={11}>No data available</td>
                            </tr>
                            : this.state.locations.map((a, i) => (
                                <tr key={a.id || a.locationId} className={a.id === "total" ? "col-bold" : undefined}>
                                    <td className="inner-edge" data-caption="Name: ">{a.name || a.location.shortName || a.location.name}</td>

                                    {showAppointments
                                        ? <>
                                            <td className="col-center inline" data-caption="Pending appointments: ">{a.appointmentPending || ""}</td>
                                            <td className="col-center inline" data-caption="Active appointments: ">{a.appointmentActive || ""}</td>
                                            <td className="col-center inline" data-caption="Canceled/Noshow appointments: ">{a.appointmentCancel || ""}</td>
                                            <td className="col-center inline inner-edge" data-caption="Complete appointments: ">{a.appointmentComplete || ""}</td>
                                            <td className="col-center inline inner-edge" data-caption="Total appointments: ">{a.appointmentTotal}</td>
                                        </> : null}
                                    <td className="col-center inline" data-caption="Pending/Active surgeries: ">{a.surgeryPending || ""}</td>
                                    <td className="col-center inline" data-caption="Canceled surgeries: ">{a.surgeryCancel || ""}</td>
                                    <td className="col-center inline inner-edge" data-caption="Complete surgeries: ">{a.surgeryComplete || ""}</td>
                                    <td className="col-center inline inner-edge" data-caption="Total surgeries: ">{a.surgeryTotal}</td>
                                </tr>
                            ))
                }
            </tbody>
        </table>;
    }
    

    private renderTableSurgeries() {
        return <table className="table-vertical">
            <thead>
                <tr>
                    <th colSpan={1} />
                    <th colSpan={8} className="col-smallest col-center">Surgeries</th>
                </tr>
                <tr>
                    <th>Name</th>
                    <th className="col-smallest col-center th-font-tiny">Outstanding</th>
                    <th className="col-smallest col-center th-font-tiny">Canceled</th>
                    <th className="col-smallest col-center th-font-tiny">Done</th>
                    <th className="col-smallest col-center col-bold th-font-tiny">Total</th>
                </tr>
            </thead>
            <thead className="table-vertical-head-small">
                <tr>
                    <th>Location Stats</th>
                </tr>
            </thead>
            <tbody>
                {
                    this.state.isLoading
                        ? <tr>
                            <td colSpan={11}>
                                {
                                    typeof this.state.isLoading === "string"
                                        ? <span className="error-text">{this.state.isLoading}</span>
                                        : <div className="spinner-center lds-dual-ring" />
                                }
                            </td>
                        </tr>
                        : this.state.locations.length < 1
                            ? <tr>
                                <td colSpan={11}>No data available</td>
                            </tr>
                            : this.state.locations.map((a, i) => (
                                <tr key={a.id || a.locationId} className={a.id === "total" ? "col-bold" : undefined}>
                                    <td className="inner-edge" data-caption="Name: ">{a.name || a.location.shortName || a.location.name}</td>

                                    <td className="col-center inline" data-caption="Pending/Active surgeries: ">{a.surgeryPending || ""}</td>
                                    <td className="col-center inline" data-caption="Canceled surgeries: ">{a.surgeryCancel || ""}</td>
                                    <td className="col-center inline inner-edge" data-caption="Complete surgeries: ">{a.surgeryComplete || ""}</td>

                                    <td className="col-center inline inner-edge" data-caption="Total surgeries: ">{a.surgeryTotal}</td>
                                </tr>
                            ))
                }
            </tbody>
        </table>;
    }

    private async search() {
        try {
            if (!this.state.searchStart || !this.state.searchEnd) {
                this.setState({
                    locations: [],
                    isLoading: false,
                });
                return;
            }

            this.setState({
                isLoading: true
            });

            let after;
            let before;
            if (this.state.dateType === "month") {
                after = DateTime.fromISO(this.state.searchMonth).startOf("month");
                before = after.endOf("month");
            }
            else {
                after = DateTime.fromISO(this.state.searchStart).startOf("day");
                before = DateTime.fromISO(this.state.searchEnd).endOf("day");
            }

            const parameters = [
                `after=${after.toISO()}`,
                `before=${before.toISO()}`,
                `mode=${this.state.dateMode}`
            ];
            let data = [];

            if (this.state.tabIndex === 0) {
                // Appointments + surgeries
                data = await V2.get<any[]>(`metrics/location?${parameters.join("&")}`);

                const sumRow = {
                    id: "total",
                    name: "Total",
                    appointmentPending: 0,
                    appointmentActive: 0,
                    appointmentCancel: 0,
                    appointmentComplete: 0,
                    appointmentTotal: 0,
                    surgeryPending: 0,
                    surgeryCancel: 0,
                    surgeryComplete: 0,
                    surgeryTotal: 0,
                };

                data.forEach(a => {
                    sumRow.appointmentPending += a.appointmentPending || 0;
                    sumRow.appointmentActive += a.appointmentActive || 0;
                    sumRow.appointmentCancel += a.appointmentCancel || 0;
                    sumRow.appointmentComplete += a.appointmentComplete || 0;
                    sumRow.appointmentTotal += a.appointmentTotal || 0;
                    sumRow.surgeryPending += a.surgeryPending || 0;
                    sumRow.surgeryCancel += a.surgeryCancel || 0;
                    sumRow.surgeryComplete += a.surgeryComplete || 0;
                    sumRow.surgeryTotal += a.surgeryTotal || 0;
                });

                data.sort((a, b) => (a.location.shortName || a.location.name).localeCompare(b.location.shortName || b.location.name));
                data.push(sumRow);
            }
            else if (this.state.tabIndex === 1) {
                // Surgery cases
                data = await V2.get<any[]>(`metrics/location/surgery?${parameters.join("&")}`);

                const sumRow = {
                    id: "total",
                    name: "Total",
                    surgeryPending: 0,
                    surgeryCancel: 0,
                    surgeryComplete: 0,
                    surgeryTotal: 0,
                };

                data.forEach(a => {
                    sumRow.surgeryPending += a.surgeryPending || 0;
                    sumRow.surgeryCancel += a.surgeryCancel || 0;
                    sumRow.surgeryComplete += a.surgeryComplete || 0;
                    sumRow.surgeryTotal += a.surgeryTotal || 0;
                });

                data.sort((a, b) => (a.location.shortName || a.location.name).localeCompare(b.location.shortName || b.location.name));
                data.push(sumRow);
            }

            this.setState({
                locations: data,
                isLoading: false,
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            [name]: value
        }, () => this.search());
    }

    private handleTabChanged(index: number) {
        this.setState({
            tabIndex: index
        }, () => this.search());
    }
}

export default withTitle("Location Stats", withAuth(MetricsLocationView));
